import React, { useCallback, useRef, useState, useMemo } from "react";
import Select, {
  components,
  type GroupBase,
  type InputActionMeta,
  type OptionProps,
  type SingleValueProps,
  type ValueContainerProps,
  type MenuProps,
  type ControlProps,
  type StylesConfig,
} from "react-select";
import CreatableSelect from "react-select/creatable";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import "./SelectInput.scss";
import { useTranslation } from "react-i18next";
import ClassNames from "classnames";
import { useClickAway } from "react-use";
import { isUndefinedOrNull, getDataAnalyticsProps } from "../../../utils";
import { debounce } from "lodash-es";
import { Tooltip, FieldViewBlock } from "../../components";
import FormFieldError from "../FormFieldError/FormFieldError";
import CheckBox from "../checkBox/CheckBox";

// Stable module-level default, mirroring the original `defaultProps`. It must
// keep a single reference across renders: `getComponents` is memoized on
// `replaceableComponents`, so a fresh `{}` default each render would recreate
// `getComponents` (and thus the Option/Menu/ValueContainer identities passed to
// react-select) on every render, remounting the option nodes mid-click and
// dropping the selection.
const EMPTY_REPLACEABLE_COMPONENTS: Record<string, any> = {};

export interface SelectOption {
  label: string;
  value: any;
  [key: string]: any;
}

type SelectValue = SelectOption | SelectOption[] | null | undefined;

export interface SelectInputProps {
  className?: string;
  isLoading?: boolean;
  options?: SelectOption[];
  label?: string | null;
  labelMini?: boolean;
  onChange?: (value: any, previousValue?: SelectValue) => void;
  value?: SelectValue;
  defaultValue?: SelectValue;
  placeholder?: string;
  isSearchable?: boolean;
  debounceTime?: number;
  isMulti?: boolean;
  customStyle?: StylesConfig<SelectOption, boolean, GroupBase<SelectOption>>;
  customFilter?: (option: any, rawInput: string) => boolean;
  isOptionDisabled?: (option: any, selectValue: any) => boolean;
  openMenuOnClick?: boolean;
  wrapperClassName?: string;
  menuWrapClassName?: string;
  size?: string;
  disabled?: boolean;
  labelTop?: boolean;
  isClearable?: boolean;
  required?: boolean;
  hideLabelHelper?: boolean;
  readOnly?: boolean;
  labelWidth?: string;
  isInline?: boolean;
  isCreatable?: boolean;
  isAsync?: boolean;
  loadOptions?: (inputValue: string) => Promise<SelectOption[]> | void;
  defaultOptions?: boolean | SelectOption[];
  setDefaultSingleOption?: boolean;
  isLarge?: boolean;
  isRound?: boolean;
  menuIsOpen?: boolean;
  error?: { message?: string; type?: string };
  showErrorWhenFocused?: boolean;
  onBlur?: () => void;
  name?: string;
  closeMenuOnSelect?: boolean;
  onInputChange?: (value: string) => void;
  inputValue?: string;
  onFocus?: () => void;
  noMargin?: boolean;
  "data-testid"?: string;
  transitionTimeout?: number;
  replaceableComponents?: Record<string, any>;
  description?: React.ReactNode;
  withHelp?: React.ReactNode;
  viewMode?: boolean;
  [key: string]: any;
}

const SelectInput: React.FC<SelectInputProps> = ({
  className = "",
  isLoading = false,
  options = [],
  label = null,
  labelMini,
  onChange,
  value,
  defaultValue,
  placeholder = "",
  isSearchable = false,
  debounceTime = 0,
  isMulti = false,
  customStyle = {},
  customFilter = undefined,
  isOptionDisabled,
  openMenuOnClick = true,
  wrapperClassName = "",
  menuWrapClassName = "",
  size = "max",
  disabled = false,
  labelTop = false,
  isClearable = false,
  required = false,
  hideLabelHelper = false,
  readOnly = false,
  labelWidth = "",
  isInline = false,
  isCreatable = false,
  isAsync = false,
  loadOptions = () => {},
  defaultOptions = false,
  setDefaultSingleOption = true,
  isLarge = false,
  isRound = false,
  menuIsOpen = false,
  error,
  showErrorWhenFocused = false,
  onBlur = () => {},
  name,
  closeMenuOnSelect,
  onInputChange = () => {},
  inputValue = "",
  onFocus = () => {},
  noMargin = false,
  "data-testid": dataTestId,
  transitionTimeout = 0,
  replaceableComponents = EMPTY_REPLACEABLE_COMPONENTS,
  description = "",
  withHelp = false,
  viewMode = false,
  ...props
}) => {
  const { t } = useTranslation();
  const [isMenuOpen, toggleMenu] = useState(false);
  const selectWrapperRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<any>(null);
  const [isFocused, setIsFocused] = useState(false);

  const onFocusChange = () => {
    if (isFocused) {
      if (onBlur) {
        onBlur();
      }
    } else {
      if (onFocus) {
        onFocus();
      }
    }
    setIsFocused(!isFocused);
  };

  useClickAway(selectWrapperRef, () => {
    if (closeMenuOnSelect !== false) {
      onInputChange("");
      setIsFocused(false);
      selectRef?.current?.blur();
      toggleMenu(false);
    } else {
      if (onBlur) {
        onBlur();
      }

      toggleMenu(true);
    }
  }, ["click"]);

  const classNameWrapper = ClassNames(
    "input-wrap",
    "input-wrap-select",
    wrapperClassName && wrapperClassName,
    {
      "has-label": label !== null,
      "has-label-top": labelTop,
      "has-label-mini": labelMini,
      "hide-label-helper": hideLabelHelper,
      "is-disabled": disabled,
      "has-read-only": readOnly,
      "has-size-xxs": size === "xxs",
      "has-size-xs": size === "xs",
      "has-size-s": size === "s",
      "has-size-m": size === "m",
      "has-size-l": size === "l",
      "has-size-xl": size === "xl",
      "has-size-xxl": size === "xxl",
      "has-size-max": size === "max",
      "has-label-width": labelWidth?.length,
      "has-size-custom": size === "custom",
      "is-inline": isInline,
      "is-large": isLarge,
      "is-round": isRound,
      "is-multi": isMulti,
      "has-no-margin": noMargin,
      "is-searchable": isSearchable,
      "has-error": !!error,
      "has-value":
        !!value || (Array.isArray(value) && (value as SelectOption[])?.length),
      "has-no-value":
        !value || (Array.isArray(value) && !(value as SelectOption[])?.length),
      "with-help": withHelp,
    },
    className
  );

  const customStyles: StylesConfig<SelectOption, boolean, GroupBase<SelectOption>> = {
    menu: (provided: Record<string, unknown>) => ({
      ...provided,
      marginTop: "4px",
    }),
    control: (provided: Record<string, unknown>) => ({
      ...provided,
      outline: "none",
      borderColor: "var(--border-color)",
      boxShadow: "none",
      ":focus": {
        outline: "none",
        borderColor: "var(--border-color)",
        boxShadow: "none",
      },
      ":hover": {
        outline: "none",
        borderColor: "var(--border-color)",
        boxShadow: "none",
      },
      ":active": {
        outline: "none",
        borderColor: "var(--border-color)",
        boxShadow: "none",
      },
    }),
    indicatorsContainer: (provided: Record<string, unknown>) => ({
      ...provided,
      padding: "5px",
    }),
    input: (provided: Record<string, unknown>) => ({
      ...provided,
      color: "var(--input-color)",
    }),
  };

  const handleOnChange = (newValue: any) => {
    if (isMulti) {
      onChange?.(newValue || []);
    } else if (
      (value as SelectOption)?.value !== newValue?.value &&
      !!onChange
    ) {
      onChange(newValue, value);
    }
  };

  const handleInputChange = (newValue: string, { action }: InputActionMeta) => {
    if (action !== "set-value" && action !== "menu-close") {
      // in order to prevent options reset (load-on-demand)
      onInputChange(newValue);
    }
  };

  const SingleValue = ({
    children,
    getValue,
    ...props
  }: SingleValueProps<SelectOption>) => {
    const selectedValues = getValue!();

    return (
      <components.SingleValue {...props}>
        <Tooltip content={selectedValues?.[0]?.label}>{children}</Tooltip>
      </components.SingleValue>
    );
  };

  const ValueContainer = ({
    children,
    getValue,
    ...props
  }: ValueContainerProps<SelectOption>) => {
    const selectedValues = getValue!();
    const newChildren: React.ReactNode[] = [
      ...(children as React.ReactNode[]),
    ];

    if (!isMulti) {
      const hasIcon = selectedValues?.[0]?.leftIcon;
      const hasImg = selectedValues?.[0]?.leftImg;
      const hasCustom = selectedValues?.[0]?.customPrefix;

      return (
        <components.ValueContainer {...props}>
          <div
            className={`single-value-wrapper ${hasIcon ? "has-icon" : ""} ${
              hasImg ? "has-image" : ""
            } ${hasCustom ? "has-custom" : ""} ${viewMode ? "is-view-mode" : ""}`}
          >
            {hasIcon && !props?.selectProps?.inputValue && (
              <i
                className={`select-icon-value seem-icon ${selectedValues?.[0]?.leftIcon}`}
              />
            )}
            {hasImg && (
              <img
                className={`select-image-value`}
                src={selectedValues?.[0]?.leftImg}
              />
            )}
            {hasCustom && selectedValues?.[0]?.customPrefix}
            {newChildren}
          </div>
        </components.ValueContainer>
      );
    }

    let maxToShow = 2;
    let length = selectedValues?.length;

    newChildren[0] = !!length
      ? length > maxToShow
        ? `${length} selected`
        : selectedValues.map((val: SelectOption) => val.label).join(", ")
      : `${placeholder}`;

    const wrappedChildren = (
      <Tooltip
        content={selectedValues
          ?.map((val: SelectOption) => val.label)
          .join(", ")}
      >
        {newChildren?.[0]}
      </Tooltip>
    );

    return length > maxToShow ? (
      <div className="select__value-container-wrap">
        <components.ValueContainer {...props}>
          <div
            className={`multi-select-items-count ${viewMode ? "is-view-mode" : ""}`}
          >
            {wrappedChildren}
          </div>
        </components.ValueContainer>
      </div>
    ) : !!length ? (
      <components.ValueContainer {...props}>
        <div
          className={`multi-select-item-selected ${viewMode ? "is-view-mode" : ""}`}
        >
          {wrappedChildren}
        </div>
      </components.ValueContainer>
    ) : (
      <components.ValueContainer {...props}>
        <div className={`multi-select-item-placeholder`}>{newChildren}</div>
      </components.ValueContainer>
    );
  };

  const Option = (optionProps: OptionProps<SelectOption>) => {
    const { innerRef } = optionProps;

    return isMulti ? (
      <>
        <div
          ref={innerRef}
          title={optionProps.data.label}
          onClick={(e) => e.stopPropagation()}
          data-testid={"select-option"}
          className={`select-option-wrap-multi ${
            optionProps.data.leftImg ? "has-left-img" : ""
          } ${optionProps.data.leftIcon ? "has-left-icon" : ""}`}
        >
          <components.Option {...optionProps}>
            {optionProps.data.leftImg && <img src={optionProps.data.leftImg} />}
            {optionProps.data.leftIcon && (
              <i
                className={`select-option-icon seem-icon seem-icon-${optionProps.data.leftIcon}`}
              />
            )}
            <CheckBox
              data-testid={`select-option-${optionProps.data.label}`}
              className={
                optionProps.data.disabled
                  ? "select-option is-disabled"
                  : "select-option"
              }
              name={optionProps.data.label}
              label={optionProps.data.label}
              checked={optionProps.isSelected}
              withBg
              hideLabelHelper
              withTooltip={{
                content: optionProps.data.label,
              }}
            />
          </components.Option>
        </div>
      </>
    ) : optionProps.data.leftImg ? (
      <div title={optionProps.data.label} data-testid={"select-option"}>
        <components.Option {...optionProps}>
          <div className="select-option-with-img-wrap">
            <img className="select-option-img" src={optionProps.data.leftImg} />
            <div className="select-option-with-img-label">
              {optionProps.data.label}
            </div>
          </div>
        </components.Option>
      </div>
    ) : optionProps.data.leftIcon ? (
      <div
        className="select-option-wrap select-option-with-icon-wrap"
        title={optionProps.data.label}
        data-testid={"select-option"}
      >
        <components.Option {...optionProps}>
          <div
            title={optionProps.data.label}
            className={`select-option-with-icon`}
          >
            <i
              className={`select-option-icon seem-icon ${optionProps.data.leftIcon}`}
            />
            <div className={`select-option-with-icon-label`}>
              {optionProps.data.label}
            </div>
          </div>
        </components.Option>
      </div>
    ) : optionProps.data.customPrefix ? (
      <components.Option {...optionProps}>
        <div
          title={optionProps.data.label}
          data-testid={"select-option"}
          className={`select-option-custom`}
        >
          <div className="select-option-custom-prefix">
            {optionProps.data.customPrefix}
          </div>
          <div className="select-option-with-custom-label">
            {optionProps.data.label}
          </div>
        </div>
      </components.Option>
    ) : optionProps.data?.customSuffix ? (
      <components.Option {...optionProps}>
        <div
          title={optionProps?.data?.label}
          data-testid={"select-option"}
          className={`select-option-custom`}
        >
          <div className="select-option-with-custom-label">
            {optionProps?.data?.label}
          </div>
          <div className="select-option-custom-suffix">
            {optionProps.data?.customSuffix}
          </div>
        </div>
      </components.Option>
    ) : (
      <div
        className="select-option-wrap"
        title={optionProps.data.label}
        data-testid={"select-option"}
      >
        <components.Option {...optionProps} />
      </div>
    );
  };

  const Menu = ({ children, ...props }: MenuProps<SelectOption>) => {
    return (
      <div
        data-testid={`${dataTestId}-menu`}
        className={`select-input-menu ${menuWrapClassName}`}
      >
        <components.Menu {...props}>{children}</components.Menu>
      </div>
    );
  };

  const getComponents = useCallback(() => {
    return {
      Option,
      Menu,
      ValueContainer,
    };
  }, [replaceableComponents]);

  const getViewModeComponents = useCallback(() => {
    return {
      Option: () => null,
      Menu: () => null,
      Control: ({ children }: ControlProps<SelectOption>) => <>{children}</>,
      DropdownIndicator: () => null,
      ValueContainer,
      SingleValue,
    };
  }, []);

  const getValue = () => {
    if (
      (isUndefinedOrNull(value) ||
        (!!value && isUndefinedOrNull((value as SelectOption)?.value))) &&
      options?.length === 1 &&
      setDefaultSingleOption
    ) {
      // If it is not a grouped option, set the default value to be the first option
      if (!options?.[0]?.options?.length) {
        onChange?.(options?.[0]);
        return options?.[0];
      }
      // If it is a grouped option, set the default value to be the first option of the grouped option
      else {
        onChange?.(options?.[0]?.options?.[0]);
        return options?.[0]?.options?.[0];
      }
    } else {
      return value;
    }
  };

  const commonProps = {
    onChange: handleOnChange,
    onInputChange: handleInputChange,
    options,
    isClearable,
    menuIsOpen: menuIsOpen || isMenuOpen,
    menuPosition: "fixed" as const,
    menuPlacement: "auto" as const,
    menuPortalTarget: document.body,
    className: `select-input-holder-wrap-inner ${
      isFocused ? "is-focused" : ""
    }`,
    classNamePrefix: "select",
    defaultValue,
    isLoading,
    name,
    filterOption: customFilter,
    isOptionDisabled,
    styles: { ...customStyles, ...customStyle },
    isSearchable,
    value: getValue(),
    placeholder,
    components: !viewMode ? getComponents() : getViewModeComponents(),
    isMulti,
    hideSelectedOptions: false,
    closeMenuOnSelect: !isMulti || closeMenuOnSelect,
    openMenuOnClick,
    isDisabled: disabled || readOnly,
    onBlur: () => {
      onFocusChange();
    },
    onFocus: () => {
      onFocusChange();
    },
    inputValue: inputValue || undefined,
  };

  const getSelectRenderMethod = useMemo(() => {
    const asyncProps = {
      ...commonProps,
      defaultOptions,
      cacheOptions: true,
      loadOptions: debounce(loadOptions, debounceTime),
    };

    const creatableProps = {
      ...commonProps,
      getNewOptionData: (inputValue: string, optionLabel: React.ReactNode) => ({
        value: inputValue,
        label: optionLabel,
        isNew: true,
      }),
      blurInputOnSelect: true,
    };

    if (isCreatable && isAsync) {
      return <AsyncCreatableSelect {...(asyncProps as any)} {...(creatableProps as any)} />;
    } else if (isAsync) {
      return <AsyncSelect {...(asyncProps as any)} />;
    } else if (isCreatable) {
      return <CreatableSelect {...(creatableProps as any)} />;
    } else {
      return (
        <Select
          {...(commonProps as any)}
          ref={selectRef}
          blurInputOnSelect={!isMulti}
          closeMenuOnScroll={(e: Event) => {
            if (
              e.target &&
              !(e?.target as HTMLElement)?.classList?.contains(
                "select__menu-list"
              )
            ) {
              setIsFocused(false);
              selectRef?.current?.blur();
              toggleMenu(false);
            }
          }}
        />
      );
    }
  }, [commonProps, defaultOptions, isAsync, isCreatable, isMulti, loadOptions]);

  return (
    <div
      ref={selectWrapperRef}
      className={`input-wrap input-wrap-select ${classNameWrapper} ${wrapperClassName}`}
      data-testid={`${dataTestId}-select-input`}
    >
      {!viewMode ? (
        <>
          {!!label && (
            <label
              style={{ width: labelWidth ? labelWidth : "" }}
              className={`input-label`}
            >
              {`${t(label)}`}
              {!required && !hideLabelHelper && (
                <span className={"label-helper"}>Optional</span>
              )}
              {withHelp && (
                <>
                  <Tooltip type="info" content={withHelp}>
                    <i className={`seem-icon seem-icon-help`} />
                  </Tooltip>
                </>
              )}
            </label>
          )}

          {!!description && (
            <div className={`input-description`}>{description}</div>
          )}
          <div
            className={`select-input-holder-wrap ${className}`}
            onClick={() => {
              if (!disabled && !readOnly) {
                if (isMenuOpen) {
                  onInputChange("");
                  setIsFocused(false);
                  selectRef?.current?.blur();
                }

                setTimeout(() => {
                  toggleMenu(!isMenuOpen);
                }, transitionTimeout);
              }
            }}
            data-testid={`${dataTestId}-management`}
            data-selected-option-label={(value as SelectOption)?.label}
            {...getDataAnalyticsProps(props)}
          >
            {getSelectRenderMethod}
          </div>

          {error && (
            <FormFieldError
              fieldName={name}
              errorMessage={error.message ?? ""}
              errorType={error.type}
              isOpen={!showErrorWhenFocused ? !isFocused : true}
            />
          )}
        </>
      ) : (
        <FieldViewBlock label={label ?? undefined}>
          {getSelectRenderMethod}
        </FieldViewBlock>
      )}
    </div>
  );
};

export default SelectInput;
